'use client';

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

/** Typing is local; blur commits once. A step button consumes the draft without a second request. */
export function useQuantityInput({
  value,
  min = 1,
  max,
  disabled,
  onCommit,
}: {
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);
  const reset = () => {
    draftRef.current = null;
    setDraft(null);
  };
  useEffect(() => {
    reset();
  }, [value, disabled]);
  const bound = (next: number) => Math.max(min, max == null ? next : Math.min(max, next));
  const read = () => {
    const text = draftRef.current?.trim().replace(',', '.');
    if (!text || !/^\d+(?:\.\d+)?$/.test(text)) return null;
    const parsed = Number(text);
    return Number.isFinite(parsed) && parsed > 0 ? bound(parsed) : null;
  };
  const commit = () => {
    const next = read();
    reset();
    if (!disabled && next != null && next !== value) onCommit(next);
  };
  return {
    current: read() ?? value,
    stepBy: (delta: number) => {
      const next = bound((read() ?? value) + delta);
      reset();
      if (!disabled && next !== value) onCommit(next);
    },
    reset,
    inputProps: {
      value: draft ?? String(value),
      disabled,
      inputMode: 'decimal' as const,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        draftRef.current = event.target.value;
        setDraft(event.target.value);
      },
      onBlur: commit,
      onFocus: (event: FocusEvent<HTMLInputElement>) => event.target.select(),
      onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.nativeEvent.isComposing) return;
        if (event.key === 'Enter' || event.key === 'Escape') {
          event.preventDefault();
          if (event.key === 'Escape') reset();
          event.currentTarget.blur();
        }
      },
    },
  };
}
